"use client";

import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import * as Yup from "yup";

import useInputValidation from "@/hooks/useInputValidation";
import CommentIcon from "@/icons/edit.svg";
import NameIcon from "@/icons/ui/forms/auth.svg";
import EmailIcon from "@/icons/ui/forms/email.svg"; // existing email icon
import { leadService } from "@/services/lead.service";
import { ContactLead } from "@/types/lead.types";
import { Button } from "@/ui/Button";
import { PhoneInputComponent } from "@/ui/Common/PhoneInput";
import { FormWrapper } from "@/ui/Forms";
import FormInput from "@/ui/Forms/FormInput";
import FormTextarea from "@/ui/Forms/FormTextarea";
import { cn } from "@/utils/cn";

import type { ContactFormProps, ContactFormValues } from "./types";

export const ContactForm: React.FC<ContactFormProps> = ({
  className,
  name = true,
  phone = true,
  email = true,
  comment = true,
  variant = "default",
  onSubmit,
}) => {
  const t = useTranslations();
  const { phoneSchema, nameSchema, emailSchema } = useInputValidation();

  const validationSchema = Yup.object({
    name: name ? nameSchema : Yup.string().optional(),
    phone: phone ? phoneSchema : Yup.string().optional(),
    email: email ? emailSchema : Yup.string().optional(),
    comment: Yup.string().optional(),
  });

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { isValid, isDirty, errors },
  } = useForm<ContactFormValues>({
    mode: "onChange",
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      comment: "",
    },
    resolver: yupResolver(validationSchema),
  });
  // Move mutation logic here (callback specific for now)
  const { mutate, isPending } = useMutation({
    mutationKey: ["createCallbackLead"],
    mutationFn: async (data: ContactFormValues) => {
      // Match previous API payload (includes type & description)
      const apiData: ContactLead = {
        name: data.name,
        phone: data.phone,
        email: data?.email || undefined,
        description: data?.comment || undefined,
      };
      try {
        const result = await leadService.createContactLead(apiData);
        return result;
      } catch (error) {
        throw error;
      }
    },
    onError: (error: any) => {
      if (error?.status === 400) return toast.error(t("Invalid data"));
      toast.error(t("Something went wrong, please try again later!"));
    },
    onSuccess: (_resp, variables) => {
      toast.success(t("Your request has been sent successfully!"));
      onSubmit?.(variables);
      reset();
    },
  });

  const internalSubmit = handleSubmit((data: ContactFormValues) =>
    mutate(data),
  );

  return (
    <form
      onSubmit={internalSubmit}
      className={clsx(
        "gap-8 lg:gap-12 grid grid-cols-1 md:grid-cols-2",
        className,
        {
          "lg:grid-cols-3": variant === "wide",
        },
      )}
    >
      <>
        {name && (
          <FormInput
            className={"!h-[5.4rem]"}
            type="text"
            label={t("Name")}
            placeholder={t("Your name")}
            required
            error={errors.name?.message}
            insideLeftComponent={
              <NameIcon className="w-[1.6rem] h-auto text-red ms-6 me-4" />
            }
            {...register("name")}
          />
        )}

        {email && (
          <FormInput
            className={"!h-[5.4rem]"}
            type="email"
            label={t("Email")}
            placeholder={t("Your email")}
            required
            error={errors.email?.message}
            insideLeftComponent={
              <EmailIcon className="w-[2rem] h-auto text-red ms-6 me-4" />
            }
            {...register("email")}
          />
        )}

        {phone && (
          <Controller
            name="phone"
            control={control}
            render={({ field }: { field: any }) => (
              <FormWrapper
                label={t("Phone number")}
                required
                error={watch("phone").length > 5 && errors.phone?.message}
              >
                <PhoneInputComponent
                  value={(field.value as string) || ""}
                  returnedValue={(val) => field.onChange(val.phone)}
                />
              </FormWrapper>
            )}
          />
        )}

        {comment && (
          <div className={"col-span-1 md:col-span-2"}>
            <FormTextarea
              label={t("Comment")}
              placeholder={t("Write what interests you")}
              error={errors.comment?.message}
              startContent={
                <CommentIcon className="w-[1.8rem] h-auto text-red ms-6 me-4 mt-[1.3rem]" />
              }
              {...register("comment")}
            />
          </div>
        )}

        <div
          className={cn("col-span-1 ", {
            "sm:col-span-2 md:justify-self-end": variant === "default",
            "md:col-span-1 md:self-center": variant === "wide",
          })}
        >
          <Button
            className={cn("w-full", {
              "sm:w-[28rem]": variant === "default",
              "md:mt-16": variant === "wide",
            })}
            color="red"
            size="md"
            type="submit"
            isDisabled={!isDirty || !isValid}
            isLoading={isPending}
            id="send-request"
          >
            {t("Send")}
          </Button>
        </div>
      </>
    </form>
  );
};

export default ContactForm;
